// Smart Indentation Engine for DSL expressions

/**
 * DSL Function Registry for smart formatting
 */
const DSL_FUNCTIONS = {
  // Mathematical operations
  'add': { params: 2, multiParam: true },
  'sub': { params: 2, multiParam: false },
  'mul': { params: 2, multiParam: true },
  'div': { params: 2, multiParam: false },
  'pow': { params: 2, multiParam: false },
  'sqrt': { params: 1, multiParam: false },
  
  // Trigonometric functions
  'sin': { params: 1, multiParam: false },
  'cos': { params: 1, multiParam: false },
  'tan': { params: 1, multiParam: false },
  
  // Calculus operations
  'diff': { params: 2, multiParam: false },
  'integrate': { params: 2, multiParam: false },
  'limit': { params: 3, multiParam: false },
  'sum': { params: 4, multiParam: false },
  
  // Vector/Matrix operations
  'vector': { params: 1, multiParam: true },
  'covector': { params: 1, multiParam: true },
  'matrix': { params: 1, multiParam: true },
  'jacobian': { params: 2, multiParam: false },
  'hessian': { params: 2, multiParam: false },
  
  // Special functions
  'eq': { params: 1, multiParam: false, specialSyntax: 'pipe' },
  'chain': { params: 1, multiParam: false, specialSyntax: 'pipe' },
  'eval': { params: 1, multiParam: false }
};

/**
 * Calculates the appropriate indentation level for a given line
 * @param {string} line - Current line content
 * @param {number} currentLevel - Current indentation level
 * @param {boolean} insideBraces - Whether we're inside curly braces
 * @returns {number} - New indentation level
 */
export const calculateIndentLevel = (line, currentLevel, insideBraces) => {
  if (!insideBraces) {
    return 0;
  }

  const trimmed = line.trim();
  if (!trimmed) {
    return currentLevel;
  }

  // Count opening and closing parentheses
  const openParens = (trimmed.match(/\(/g) || []).length;
  const closeParens = (trimmed.match(/\)/g) || []).length;
  const netParens = openParens - closeParens;

  // Detect function calls
  const functionMatch = trimmed.match(/^(\w+)\s*\(/);
  if (functionMatch) {
    const funcName = functionMatch[1];
    if (DSL_FUNCTIONS[funcName]) {
      return currentLevel + 1;
    }
  }

  // Handle closing parentheses
  if (closeParens > openParens) {
    return Math.max(0, currentLevel - (closeParens - openParens));
  }

  // Handle comma-separated parameters
  if (trimmed.startsWith(',')) {
    return currentLevel;
  }

  return currentLevel + netParens;
};

/**
 * Generates indentation string
 * @param {number} level - Indentation level
 * @param {string} indentChar - Character to use for indentation (default: 2 spaces)
 * @returns {string} - Indentation string
 */
export const generateIndent = (level, indentChar = '  ') => {
  return indentChar.repeat(Math.max(0, level));
};

/**
 * Formats a DSL expression with smart indentation
 * @param {string} expression - Raw DSL expression
 * @returns {string} - Formatted expression with proper indentation
 */
export const formatDSLExpression = (expression) => {
  if (!expression || typeof expression !== 'string') {
    return expression;
  }

  const lines = expression.split('\n');
  const formatted = [];
  let indentLevel = 0;
  let insideBraces = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Check for brace boundaries
    if (trimmed.includes('{')) {
      insideBraces = true;
      formatted.push(line); // Keep original formatting for brace line
      continue;
    }
    if (trimmed.includes('}')) {
      insideBraces = false;
      indentLevel = 0;
      formatted.push(line); // Keep original formatting for brace line
      continue;
    }

    if (!trimmed) {
      formatted.push(''); // Preserve empty lines
      continue;
    }

    // Calculate and apply indentation
    const newIndentLevel = calculateIndentLevel(trimmed, indentLevel, insideBraces);
    const indent = generateIndent(newIndentLevel);
    formatted.push(indent + trimmed);
    
    indentLevel = newIndentLevel;
  }

  return formatted.join('\n');
};

/**
 * Auto-indentation for curly brace contexts (for Enter key)
 * @param {string} currentLine - Current line content
 * @param {number} cursorPos - Cursor position in line
 * @param {boolean} insideBraces - Whether inside curly braces
 * @returns {string} - Indentation to insert
 */
export const getAutoIndent = (currentLine, cursorPos, insideBraces) => {
  if (!insideBraces) {
    return '';
  }

  const beforeCursor = currentLine.substring(0, cursorPos);
  const trimmedBefore = beforeCursor.trim();
  
  // Base indentation for being inside braces
  let indentLevel = 1;
  
  // Count nested parentheses for additional indentation
  const openParens = (beforeCursor.match(/\(/g) || []).length;
  const closeParens = (beforeCursor.match(/\)/g) || []).length;
  const netParens = openParens - closeParens;
  
  // Add indentation for unclosed parentheses
  indentLevel += Math.max(0, netParens);
  
  // Check if we're inside a function call
  const functionMatch = trimmedBefore.match(/(\w+)\s*\([^)]*$/);
  if (functionMatch) {
    const funcName = functionMatch[1];
    if (DSL_FUNCTIONS[funcName] && DSL_FUNCTIONS[funcName].multiParam) {
      indentLevel += 1; // Extra indent for multi-parameter functions
    }
  }
  
  return generateIndent(indentLevel);
};